import React from 'react';
import styled from 'styled-components';
import { Trash2, ShoppingCart } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom'; 

const CartContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  margin-right: 15px;
  border-radius: 4px;
`;

const ItemDetails = styled.div`
  flex-grow: 1;
`;

const ItemName = styled.h3`
  font-size: 16px;
  margin: 0 0 5px 0;
  color: #2c3e50;
`;

const ItemPrice = styled.p`
  font-size: 14px;
  color: #27ae60;
  font-weight: bold;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  margin-top: 5px;
`;

const QuantityButton = styled.button`
  background-color: #ecf0f1;
  border: 1px solid #bdc3c7;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  padding: 0;
  line-height: 1;

  &:hover {
    background-color: #bdc3c7;
  }
  &:active {
    background-color: #a1a8ad;
  }
`;
const QuantityButtonText = styled.span`
  display: block;
  transform: translateY(-1px);
`;

const QuantityDisplay = styled.span`
  margin: 0 10px;
  font-size: 14px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #c0392b;
  }
`;

const CartSummary = styled.div`
  margin-top: 20px;
  text-align: right;
`;

const TotalPrice = styled.p`
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
`;

const CheckoutButton = styled.button`
  display: inline-block;
  background-color: #27ae60;
  color: white;
  padding: 10px 20px;
  text-decoration: none;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  margin-top: 10px;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #2ecc71;
  }
`;

const EmptyCartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
`;

const EmptyCartIcon = styled.div`
  color: #27ae60;
  margin-bottom: 20px;
`;

const EmptyCartText = styled.p`
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 20px;
`;

const ContinueShoppingButton = styled.button`
  background-color: #27ae60;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #2ecc71;
  }
`;

const Cart = ({ showToast, onProceedToCheckout, showCheckoutButton = true, onContinueShopping = () => {} }) => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  const handleQuantityChange = (item, change) => {
    const newQuantity = item.isPrePacked 
      ? item.quantity + change 
      : parseFloat((item.selectedWeight + change).toFixed(1));
    
    const minPurchase = item.min_purchase_quantity || 1; // Default to 1 if not specified
    const unit = item.min_purchase_unit || (item.isPrePacked ? 'units' : item.weight_unit || 'kg');

    if (newQuantity >= minPurchase) {
      updateQuantity(item.id, newQuantity);
    } else {
      showToast(`Cannot reduce ${item.name} below ${minPurchase} ${unit}. Minimum purchase quantity required.`, 'error');
    }
  };

  const calculateItemPrice = (item) => {
    return item.price * (item.isPrePacked ? item.quantity : item.selectedWeight);
  };

  const totalPrice = cartItems.reduce((sum, item) => sum + calculateItemPrice(item), 0);

  const handleContinueShopping = () => {
    onContinueShopping();
    navigate('/');
  };

  if (cartItems.length === 0) {
    return (
      <EmptyCartContainer>
        <EmptyCartIcon>
          <ShoppingCart size={48} />
        </EmptyCartIcon>
        <EmptyCartText>Your cart is empty</EmptyCartText>
        <ContinueShoppingButton onClick={handleContinueShopping}>
          Continue shopping
        </ContinueShoppingButton>
      </EmptyCartContainer>
    );
  }

  return (
    <CartContainer>
      {cartItems.map(item => (
        <CartItem key={item.id}>
          <ItemImage src={item.image} alt={item.name} />
          <ItemDetails>
            <ItemName>{item.name}</ItemName>
            <ItemPrice>
              ${calculateItemPrice(item).toFixed(2)}
            </ItemPrice>
            <QuantityControl>
              <QuantityButton 
                onClick={() => handleQuantityChange(item, item.isPrePacked ? -1 : -0.5)} 
                aria-label="Decrease quantity"
              >
                 <QuantityButtonText>−</QuantityButtonText>
              </QuantityButton>
              <QuantityDisplay>
                {item.isPrePacked ? item.quantity : item.selectedWeight.toFixed(1)} {item.isPrePacked ? 'units' : item.weight_unit}
              </QuantityDisplay>
              <QuantityButton 
                onClick={() => handleQuantityChange(item, item.isPrePacked ? 1 : 0.5)} 
                aria-label="Increase quantity"
              >
               <QuantityButtonText>+</QuantityButtonText>
               
              </QuantityButton>
            </QuantityControl>
          </ItemDetails>
          <RemoveButton onClick={() => removeFromCart(item.id)} aria-label="Remove item">
            <Trash2 size={18} />
          </RemoveButton>
        </CartItem>
      ))}
      <CartSummary>
        <TotalPrice>Total: ${totalPrice.toFixed(2)}</TotalPrice>
        {showCheckoutButton && (
          <CheckoutButton onClick={onProceedToCheckout}>
            Checkout
          </CheckoutButton>
        )}
      </CartSummary>
    </CartContainer>
  );
};

export default Cart;